import React, { useState, useEffect, useRef } from 'react';
import type { CashcardProduct, CashcardOption } from '@/lib/TicketApi';
import { shopVar } from '@/lib/shopStyles';
import { Money } from '@/lib/money';
import { useLanguage } from '@/providers/LanguageProvider';
import { useCart } from '@/providers/CartProvider';

interface CashcardProductProps {
    product: CashcardProduct;
}

const CashcardProduct: React.FC<CashcardProductProps> = ({ product }) => {
    const { currentLanguage: lang } = useLanguage();
    const { setProductQuantity, cart } = useCart();
    const [selected, setSelected] = useState<number | 'custom' | null>(null);
    const [rawCustomAmount, setRawCustomAmount] = useState<string>('');
    const [validationError, setValidationError] = useState<string | null>(null);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const didMount = useRef(false);
    const isEditingCustom = useRef(false);
    const lastInvalidInput = useRef(false);

    const options: CashcardOption[] = product.cashcard.options || [];
    const custom = product.cashcard.customTopup != null;
    const minAmount = product.cashcard.customTopup?.lowerLimit.amount ?? 0;
    const maxAmount = product.cashcard.customTopup?.upperLimit.amount ?? Infinity;

    // Parse euro input to cents (whole euros only)
    function parseEuroInputToCents(input: string): number {
        if (!input) return 0;
        const safe = input.replace(/\D/g, '');
        const intVal = parseInt(safe, 10);
        if (isNaN(intVal)) return 0;
        return intVal * 100;
    }

    // Format cents to whole euro string for display
    function formatCentsToEuroString(amount: number): string {
        if (isNaN(amount)) return '';
        return String(Math.floor(amount / 100));
    }

    // Sync with cart on mount or cart change
    useEffect(() => {
        if (!cart) return;
        if (isEditingCustom.current) return;
        if (lastInvalidInput.current) return;
        const cartProduct = cart.products.find(p => p.productId === product.productId);
        if (!cartProduct || !cartProduct.settings) {
            setSelected(null);
            setRawCustomAmount('');
            return;
        }
        const { amount, currency } = cartProduct.settings;
        if (typeof amount !== 'number' || !currency) {
            setSelected(null);
            setRawCustomAmount('');
            return;
        }
        const idx = options.findIndex(opt => opt.amount === amount && opt.currency === currency);
        if (idx !== -1) {
            setSelected(idx);
            setRawCustomAmount('');
            setValidationError(null);
        } else {
            setSelected('custom');
            const euroString = formatCentsToEuroString(amount);
            setRawCustomAmount(euroString);
            setValidationError(null);
        }
        didMount.current = true;
    }, [cart, product.productId, options, lang]);

    // Handle option select
    const handleSelect = (idx: number | 'custom') => {
        if (selected === idx) {
            setProductQuantity(product.productId, 0);
            setSelected(null);
            setRawCustomAmount('');
            setValidationError(null);
            lastInvalidInput.current = false;
            return;
        }
        setSelected(idx);
        setValidationError(null);
        lastInvalidInput.current = false;
        if (idx === 'custom') {
            setTimeout(() => inputRef.current?.focus(), 0);
            return;
        }
        const option = options[idx as number];
        setProductQuantity(product.productId, 1, {
            amount: option.amount,
            currency: option.currency
        });
        setRawCustomAmount('');
    };

    // Validate and update cart (debounced)
    const validateAndUpdateCart = (value: string) => {
        const cents = parseEuroInputToCents(value);
        if (!value || cents <= 0) {
            setValidationError(null);
            setProductQuantity(product.productId, 0);
            lastInvalidInput.current = false;
            return;
        }
        if (cents < minAmount) {
            setValidationError(`Minimum top-up is €${Math.ceil(minAmount / 100)}`);
            setProductQuantity(product.productId, 0);
            lastInvalidInput.current = true;
            return;
        }
        if (cents > maxAmount) {
            setValidationError(`Maximum top-up is €${Math.floor(maxAmount / 100)}`);
            setProductQuantity(product.productId, 0);
            lastInvalidInput.current = true;
            return;
        }
        setValidationError(null);
        setProductQuantity(product.productId, 1, {
            amount: cents,
            currency: product.price.currency
        });
        setSelected('custom');
        lastInvalidInput.current = false;
    };

    // Handle custom input change (whole euros only)
    const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        isEditingCustom.current = true;
        const value = e.target.value.replace(/\D/g, ''); // Only digits
        setRawCustomAmount(value);
        // Debounce cart update and validation
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            validateAndUpdateCart(value);
            isEditingCustom.current = false;
        }, 500);
    };

    // Format value on blur (no formatting needed for whole euros)
    const handleCustomAmountBlur = () => {};

    // Keep input value in sync with rawCustomAmount while editing
    const inputValue = selected === 'custom' ? rawCustomAmount : '';

    const hasDescription = product.description && product.description.trim() !== '';

    return (
        <div
            className="border rounded-md px-4 pt-4 pb-2 mb-4 shadow-md last:mb-0 overflow-hidden"
            style={{ borderColor: shopVar('--shop-borderColor') }}
        >
            <div className="flex items-start border-b border-dashed w-full pb-4" style={{ borderColor: shopVar('--shop-borderColor') }}>
                <div className="flex-grow pr-2">
                    <div className="flex items-center">
                        <h2 className="text-base font-bold mr-2" style={{ color: shopVar('--shop-textColor') }}>{product.name}</h2>
                    </div>
                    {hasDescription && (
                        <p className="font-normal text-xs tracking-tight mt-1" style={{ color: shopVar('--shop-textColor') }}>
                            {product.description}
                        </p>
                    )}
                </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
                {options.map((opt: CashcardOption, idx: number) => (
                    <button
                        key={idx}
                        className={`flex-1 min-w-[80px] max-w-[160px] px-4 py-2 rounded border text-center font-semibold transition-colors ${selected === idx ? 'ring-2 ring-black' : ''}`}
                        style={{
                            background: selected === idx ? shopVar('--shop-addToCartButtonColor') : shopVar('--shop-panelBackgroundColor'),
                            color: selected === idx ? shopVar('--shop-addToCartButtonTextColor') : shopVar('--shop-textColor'),
                            borderColor: shopVar('--shop-borderColor'),
                        }}
                        onClick={() => handleSelect(idx)}
                        type="button"
                    >
                        {Money.fromInteger(opt.amount, opt.currency).display(lang)}
                    </button>
                ))}
                {custom && (
                    <button
                        className={`flex-1 min-w-[80px] max-w-[160px] px-4 py-2 rounded border text-center font-semibold transition-colors ${selected === 'custom' ? 'ring-2 ring-black' : ''}`}
                        style={{
                            background: selected === 'custom' ? shopVar('--shop-addToCartButtonColor') : shopVar('--shop-panelBackgroundColor'),
                            color: selected === 'custom' ? shopVar('--shop-addToCartButtonTextColor') : shopVar('--shop-textColor'),
                            borderColor: shopVar('--shop-borderColor'),
                        }}
                        onClick={() => handleSelect('custom')}
                        type="button"
                    >
                        Choose custom
                    </button>
                )}
            </div>
            {selected === 'custom' && (
                <div className="mt-4">
                    <label htmlFor="custom-amount-input" className="block text-xs font-medium mb-1" style={{ color: shopVar('--shop-textColor') }}>
                        Custom amount
                    </label>
                    <div className="flex gap-2 items-center">
                        <span className="text-base font-semibold" style={{ color: shopVar('--shop-textColor') }}>€</span>
                        <input
                            id="custom-amount-input"
                            ref={inputRef}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={inputValue}
                            onChange={handleCustomAmountChange}
                            onBlur={handleCustomAmountBlur}
                            placeholder="0"
                            className="flex-1 px-4 py-2 border rounded focus:ring-2 focus:ring-black text-base"
                            style={{ borderColor: shopVar('--shop-borderColor'), color: shopVar('--shop-textColor'), background: shopVar('--shop-panelBackgroundColor') }}
                            aria-invalid={!!validationError}
                            aria-describedby={validationError ? 'custom-amount-error' : undefined}
                            autoComplete="off"
                        />
                    </div>
                    {validationError && (
                        <div id="custom-amount-error" className="text-red-600 text-xs mt-1" role="alert">
                            {validationError}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CashcardProduct; 